import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { initGA, trackEvent, updateConsent } from '../utils/analytics';

const CookieConsent = ({ language }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  // Granular settings state
  const [showCustomize, setShowCustomize] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [consentSettings, setConsentSettings] = useState({
    necessary: true, // Always true
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Prevent execution during pre-rendering (react-snap)
    if (typeof navigator !== 'undefined' && navigator.userAgent.includes("ReactSnap")) return;

    setIsMounted(true);
    const savedConsent = localStorage.getItem('cookieConsent');

    if (!savedConsent) {
      setIsVisible(true);
      // Initialize with default denied settings for Consent Mode v2
      initGA();
    } else {
      try {
        if (savedConsent === 'true') {
          const granted = { necessary: true, analytics: true, marketing: true };
          setConsentSettings(granted);
          initGA(granted);
        } else if (savedConsent === 'false') {
          const minGranted = { necessary: true, analytics: false, marketing: false };
          setConsentSettings(minGranted);
          initGA(minGranted);
        } else {
          const parsed = JSON.parse(savedConsent);
          setConsentSettings(parsed);
          initGA(parsed);
        }
      } catch (e) {
        setIsVisible(true);
        initGA();
      }
    }
  }, []);

  useEffect(() => {
    if (isVisible && isMounted) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isVisible, isMounted]);

  // Listen for custom event to re-open settings from footer
  useEffect(() => {
    const handleOpenSettings = () => {
      setShowCustomize(true);
    };
    window.addEventListener('openCookieSettings', handleOpenSettings);
    return () => window.removeEventListener('openCookieSettings', handleOpenSettings);
  }, []);

  // Handle "Accept All"
  const handleAcceptAll = () => {
    const allGranted = { necessary: true, analytics: true, marketing: true };
    setConsentSettings(allGranted);
    localStorage.setItem('cookieConsent', JSON.stringify(allGranted));
    updateConsent(allGranted);
    trackEvent("Consent", "Accept All", "Banner");
    setIsVisible(false);
  };

  // Handle "Decline" / "Necessary Only" (from main banner)
  const handleDeclineAll = () => {
    const minGranted = { necessary: true, analytics: false, marketing: false };
    setConsentSettings(minGranted);
    localStorage.setItem('cookieConsent', JSON.stringify(minGranted));
    updateConsent(minGranted);
    trackEvent("Consent", "Decline", "Banner");
    setIsVisible(false);
  };

  // Handle "Save Preferences" (from customize view)
  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(consentSettings));
    updateConsent(consentSettings);
    trackEvent("Consent", "Save Preferences", consentSettings.analytics ? "Analytics On" : "Analytics Off");
    setIsVisible(false);
    setShowCustomize(false);
  };

  const toggleSetting = (key) => {
    setConsentSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Reset click count after 2 seconds of inactivity
  useEffect(() => {
    if (clickCount === 0) return;
    const timer = setTimeout(() => setClickCount(0), 2000);
    return () => clearTimeout(timer);
  }, [clickCount]);

  const handleBackdropClick = (e) => {
    e.stopPropagation();
    const newCount = clickCount + 1;
    if (newCount >= 5) {
      handleDeclineAll();
      setClickCount(0);
    } else {
      setClickCount(newCount);
      console.log(`Backdrop clicks: ${newCount}/5`);
    }
  };

  // DO NOT RENDER DURING PRE-RENDERING
  // Also, return null if NEITHER the banner (isVisible) NOR the settings (showCustomize) are active
  if (!isMounted || (!isVisible && !showCustomize) || (typeof navigator !== 'undefined' && navigator.userAgent.includes("ReactSnap"))) {
    return null;
  }

  const text = {
    en: {
      message: "We use cookies to ensure you get the best experience on our website.",
      customize: "Customize",
      acceptAll: "Accept All",
      decline: "Decline",
      save: "Save Preferences",
      privacyPolicy: "Privacy Policy",
      settingsTitle: "Cookie Preferences",
      necessary: "Necessary (Always Active)",
      analytics: "Analytics",
      marketing: "Marketing",
      descNecessary: "Essential for the website to function correctly.",
      descAnalytics: "Helps us understand how you use the website (Google Analytics).",
      descMarketing: "Used for targeted advertising."
    },
    cs: {
      message: "Používáme cookies, abychom zajistili co nejlepší zážitek z našeho webu.",
      customize: "Nastavení",
      acceptAll: "Přijmout vše",
      decline: "Odmítnout",
      save: "Uložit nastavení",
      privacyPolicy: "Zásady ochrany osobních údajů",
      settingsTitle: "Nastavení Cookies",
      necessary: "Nezbytné (Vždy aktivní)",
      analytics: "Analytické",
      marketing: "Marketingové",
      descNecessary: "Nutné pro správné fungování webu.",
      descAnalytics: "Pomáhají nám pochopit, jak web používáte (Google Analytics).",
      descMarketing: "Používají se pro reklamu a marketing."
    },
    de: {
      message: "Wir verwenden Cookies, um sicherzustellen, dass Sie das beste Erlebnis auf unserer Website erhalten.",
      customize: "Einstellungen",
      acceptAll: "Alle akzeptieren",
      decline: "Ablehnen",
      save: "Einstellungen speichern",
      privacyPolicy: "Datenschutzerklärung",
      settingsTitle: "Cookie-Einstellungen",
      necessary: "Notwendig (Immer aktiv)",
      analytics: "Analyse",
      marketing: "Marketing",
      descNecessary: "Erforderlich für das ordnungsgemäße Funktionieren der Website.",
      descAnalytics: "Helfen uns zu verstehen, wie Sie die Website nutzen (Google Analytics).",
      descMarketing: "Wird für gezielte Werbung verwendet."
    }
  };

  const t = text[language] || text.en;

  if (showCustomize) {
    return (
      <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setShowCustomize(false)} />
        <div className="bg-white text-gray-900 rounded-xl shadow-2xl max-w-lg w-full relative z-10 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-xl font-bold">{t.settingsTitle}</h2>
          </div>

          <div className="p-6 space-y-4">
            {/* Necessary */}
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold">{t.necessary}</div>
                <div className="text-sm text-gray-500">{t.descNecessary}</div>
              </div>
              <input type="checkbox" checked={true} disabled className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded" />
            </div>

            {/* Analytics */}
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold">{t.analytics}</div>
                <div className="text-sm text-gray-500">{t.descAnalytics}</div>
              </div>
              <input
                type="checkbox"
                checked={consentSettings.analytics}
                onChange={() => toggleSetting('analytics')}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>

            {/* Marketing */}
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold">{t.marketing}</div>
                <div className="text-sm text-gray-500">{t.descMarketing}</div>
              </div>
              <input
                type="checkbox"
                checked={consentSettings.marketing}
                onChange={() => toggleSetting('marketing')}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
          </div>

          <div className="p-6 bg-gray-50 flex justify-end gap-3 rounded-b-xl">
            <button
              onClick={() => setShowCustomize(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
            >
              {language === 'cs' ? 'Zpět' : (language === 'de' ? 'Zurück' : 'Back')}
            </button>
            <button
              onClick={handleSavePreferences}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold shadow-sm transition-all"
            >
              {t.save}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-md z-[99998] pointer-events-auto"
        onClick={handleBackdropClick}
      />

      <div className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white p-6 shadow-2xl z-[99999] flex flex-col md:flex-row items-center justify-between gap-6 pointer-events-auto border-t border-gray-700">
        <div className="text-sm md:text-base leading-relaxed max-w-2xl">
          {t.message}{" "}
          <Link
            to={`/${language}/gdpr`}
            className="underline hover:text-blue-400 ml-1 font-medium transition-colors"
          >
            {t.privacyPolicy}
          </Link>
        </div>
        <div className="flex flex-wrap gap-3 shrink-0 justify-center md:justify-end">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowCustomize(true);
            }}
            className="bg-gray-800 hover:bg-gray-700 text-gray-200 px-4 py-2.5 rounded-lg text-sm font-medium transition-all border border-gray-600"
          >
            {t.customize}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleDeclineAll();
            }}
            className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-all"
          >
            {t.decline}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleAcceptAll();
            }}
            className="bg-green-600 hover:bg-green-500 text-white px-8 py-2.5 rounded-lg text-sm font-bold shadow-lg transition-all transform hover:scale-105"
          >
            {t.acceptAll}
          </button>
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
